// src/app/modules/demandes/HorsCatalogueManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../../RessourcesStyles/HorsCatalogue.css";

import UsersPickerModal from "./UsersPickerModal";
import BulkUsersFieldsModal from "./BulkUsersFieldsModal";

import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchDomainesRequest, fetchThemesRequest } from "../../../../../redux/actions/paramDomainesThemesActions";
import { getDirectReportsRequest } from "../../../../../redux/actions/userActions";
import { createDemandeManagerBulkRequest } from "../../../../../redux/actions/demandesActions";

import { Users, ArrowRight } from "lucide-react";

const initial = {
    campagneId: "",
    domaineId: "",
    themeId: "",
    action: "",
    description: "",
};

export default function HorsCatalogueManager({
                                                 campagneId,
                                                 hideCampagneSelect = false,
                                                 onCancel,
                                                 onSuccess,
                                             }) {
    const dispatch = useDispatch();
    const uid = sessionStorage.getItem("uid");

    const campagnesState = useSelector((s) => s.campagnes || {});
    const refState = useSelector((s) => s.paramDomainesThemes || {});
    const usersState = useSelector((s) => s.user || {});

    const campagnes = campagnesState.visibleList || campagnesState.list || [];
    const domaines = refState.domaines || refState.domainesList || [];
    const themes = refState.themes || refState.themesList || [];
    const teamUsers = usersState.directReports || [];

    const [form, setForm] = useState(initial);
    const [localCampagneId, setLocalCampagneId] = useState("");
    const campagneIdEff = hideCampagneSelect ? campagneId : localCampagneId;

    const [openPick, setOpenPick] = useState(false);
    const [openFields, setOpenFields] = useState(false);

    const [pickedUsers, setPickedUsers] = useState([]);

    useEffect(() => {
        if (uid) dispatch(getDirectReportsRequest(uid));
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch, uid]);

    useEffect(() => {
        if (!hideCampagneSelect && !localCampagneId && campagnes?.length) {
            setLocalCampagneId(String(campagnes[0].id));
        }
    }, [hideCampagneSelect, campagnes, localCampagneId]);

    // sync form.campagneId
    useEffect(() => {
        if (campagneIdEff) setForm((s) => ({ ...s, campagneId: String(campagneIdEff) }));
    }, [campagneIdEff]);

    const themesFiltered = useMemo(() => {
        const did = form.domaineId ? Number(form.domaineId) : null;
        if (!did) return themes || [];
        return (themes || []).filter((t) => Number(t?.domaineId || t?.domaine?.id) === did);
    }, [themes, form.domaineId]);

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const openSelector = () => {
        if (!campagneIdEff) {
            Swal.fire({
                icon: "warning",
                text: "Sélectionne une campagne.",
                target: document.querySelector(".tcvModal") || "body",
            });
            return;
        }
        if (!form.action?.trim()) {
            Swal.fire({
                icon: "warning",
                text: "Action / Intitulé est obligatoire.",
                target: document.querySelector(".tcvModal") || "body",
            });
            return;
        }
        setOpenPick(true);
    };

    const onConfirmUsers = (ids) => {
        if (!ids?.length) {
            Swal.fire({
                icon: "warning",
                text: "Sélectionne au moins un collaborateur.",
                target: document.querySelector(".tcvModal") || "body",
            });
            return;
        }
        const uniqueIds = Array.from(new Set(ids || []));
        const users = (teamUsers || []).filter((u) => uniqueIds.includes(u.id));
        setPickedUsers(users);
        setOpenPick(false);
        setOpenFields(true);
    };

    const onSubmitFields = (rows) => {
        const payload = {
            campagneId: Number(campagneIdEff),
            type: "HORS_CATALOGUE",
            domaineId: form.domaineId ? Number(form.domaineId) : null,
            themeId: form.themeId ? Number(form.themeId) : null,
            action: form.action.trim(),
            description: form.description?.trim() || null,
            items: (rows || []).map((r) => ({
                targetUserId: Number(r.targetUserId),
                mode: r.mode || null,
                period: r.period || null,
                niveau: r.niveau || null,
                certifiante: !!r.certifiante,
                priorite: r.priorite != null ? Number(r.priorite) : null,
                objectifs: r.objectifs || null,
                objectifs2: r.objectifs2 || null,
                objectifs3: r.objectifs3 || null,
            })),
        };

        dispatch(createDemandeManagerBulkRequest(payload));
        setOpenFields(false);
        setPickedUsers([]);
        if (onSuccess) onSuccess();
    };

    return (
        <div className={`hc-page hcmgr-page ${hideCampagneSelect ? "is-embedded" : ""}`.trim()}>
            <div className="hc-card hcrf-card">
                {/* ── Informations principales ── */}
                <p className="hc-section-label">Informations principales</p>

                <div className="hc-field">
                    <label>Action / Intitulé *</label>
                    <input
                        value={form.action}
                        onChange={(e) => update("action", e.target.value)}
                        placeholder="Ex: Formation spécifique..."
                    />
                </div>

                <div className="hc-field">
                    <label>Description</label>
                    <textarea
                        value={form.description}
                        onChange={(e) => update("description", e.target.value)}
                        rows={4}
                        placeholder="Décrivez les besoins de votre équipe..."
                    />
                </div>

                {/* ── Paramètres ── */}
                <p className="hc-section-label">Paramètres</p>

                <div className="hc-grid-3">
                    {!hideCampagneSelect && (
                        <div className="hc-field">
                            <label>Campagne *</label>
                            <select value={campagneIdEff || ""} onChange={(e) => setLocalCampagneId(e.target.value)}>
                                <option value="">—</option>
                                {(campagnes || []).map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.nom || c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="hc-field">
                        <label>Domaine</label>
                        <select
                            value={form.domaineId}
                            onChange={(e) => {
                                update("domaineId", e.target.value);
                                update("themeId", "");
                            }}
                        >
                            <option value="">—</option>
                            {(domaines || []).map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.nom || d.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="hc-field">
                        <label>Thème</label>
                        <select value={form.themeId} onChange={(e) => update("themeId", e.target.value)}>
                            <option value="">—</option>
                            {(themesFiltered || []).map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.nom || t.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* ── Actions ── */}
                <div className="hc-actions">
                    {onCancel && (
                        <button className="hc-btn hc-btn--secondary" type="button" onClick={onCancel}>
                            Annuler
                        </button>
                    )}
                    <button
                        className="hc-btn hcrf-btn-primary"
                        type="button"
                        onClick={openSelector}
                        disabled={!campagneIdEff}
                    >
                        <Users size={16} />
                        Choisir les collaborateurs
                        <ArrowRight size={16} />
                    </button>
                </div>

                {!campagneIdEff && (
                    <div className="demande-hint">⚠️ Sélectionne d'abord une campagne.</div>
                )}
            </div>

            <UsersPickerModal
                open={openPick}
                onClose={() => setOpenPick(false)}
                users={teamUsers}
                title="Sélectionner des collaborateurs (équipe)"
                onConfirm={onConfirmUsers}
            />

            <BulkUsersFieldsModal
                open={openFields}
                onClose={() => setOpenFields(false)}
                users={pickedUsers}
                title="Champs par collaborateur (Hors catalogue - Manager)"
                onSubmit={onSubmitFields}
            />
        </div>
    );
}
